import React, { useState, useEffect } from 'react';
import { useParams, Redirect } from 'react-router-dom';
import { getIncomeGain } from 'config/api';
import {
  IncomeGainProps,
  IncomeGainItemProps,
  getSummaryIncomeGainAmount,
} from 'pages/DividendDeposit';
import { formatNumber, formatDate } from 'utils/formatter';
import { useCustomizeHeaderContext } from 'contexts/CustomizeHeaderContext';
import { DividendDetailList } from 'components/features/dividendDeposit/DividendClassification';
import {
  DividendDepositModal,
  getMonthArray,
} from 'components/features/dividendDeposit/DividendTransition';
import {
  DividendPieChart,
  DividendPieChartNoData,
} from 'components/features/dividendDeposit/DividendChart';
import { DividendDepositLoading } from 'components/features/dividendDeposit/DividendError';
import { yyyymmddToDate } from 'components/features/new-report/daily-report/DailyReport';
import { isIosApp } from 'components/features/utils/userStatus';
import { DividendHeader, UpdateTimeComment } from './DividendPrediction';
import { formatYM } from './DividendDetailPrediction';

export const getSortIncomeGainAmountData = (
  incomeGainData: IncomeGainItemProps[]
): IncomeGainItemProps[] => {
  const sortData = incomeGainData.sort((first, second) => {
    const firstAmount = first.amount ?? 0;
    const secondAmount = second.amount ?? 0;
    if (firstAmount < secondAmount) {
      return 1;
    } else if (firstAmount > secondAmount) {
      return -1;
    } else {
      return 0;
    }
  });

  return sortData;
};

export const HightDividendList = ({
  tag,
  className,
}: {
  tag: string;
  className?: string;
}) => {
  return (
    <>
      <div
        className={`flex flex-row items-center ${className} text-bluegray-700`}>
        <div className="icon-search" />
        <div className="ml-2 text-lg font-bold">高配当銘柄を探す</div>
      </div>
      <div className="mt-2 py-4 px-4 text-bluegray-700 bg-white">
        <a
          href="https://minkabu.jp/stock/search?view=result&page=1&sort_key=roe&order=desc&minimum_purchase_price[0]=min&minimum_purchase_price[1]=max&market_capitalization[0]=200&market_capitalization[1]=max&per[0]=min&per[1]=15&pbr[0]=min&pbr[1]=1&dividend_yield[0]=3&dividend_yield[1]=max&capital_adequacy_ratio[0]=40&capital_adequacy_ratio[1]=max&roe[0]=10&roe[1]=max"
          id="dividendprediction_high_value"
          target="_blank"
          rel="noreferrer">
          <div
            className="py-3 px-4 flex flex-row items-center bg-bluegray-50 border border-gray-300 rounded-lg"
            id={`click_${tag}_wariyasu`}>
            <div>
              <img
                src="/static/images/dividend/kohaito_icon1.svg"
                alt="高配当アイコン1"
              />
            </div>
            <div className="ml-2 text-sm font-bold">高配当×割安株</div>
            <div className="icon-external-link text-xs ml-auto text-gray-500" />
          </div>
        </a>
        <a
          href="https://minkabu.jp/stock/search?view=result&page=1&sort_key=dividend_yield&order=desc&minimum_purchase_price[0]=min&minimum_purchase_price[1]=10&market_capitalization[0]=200&market_capitalization[1]=max&dividend_yield[0]=3&dividend_yield[1]=max&capital_adequacy_ratio[0]=40&capital_adequacy_ratio[1]=max&roe[0]=10&roe[1]=max"
          id="dividendprediction_high_100k"
          target="_blank"
          rel="noreferrer">
          <div
            className="mt-2 py-3 px-4 flex flex-row items-center bg-bluegray-50 border border-gray-300 rounded-lg"
            id={`click_${tag}_haito`}>
            <div>
              <img
                src="/static/images/dividend/kohaito_icon2.svg"
                alt="高配当アイコン2"
              />
            </div>
            <div className="ml-2 text-sm font-bold">
              高配当×10万円以下で配当狙い
            </div>
            <div className="icon-external-link text-xs ml-auto text-gray-500" />
          </div>
        </a>
        <a
          href="https://minkabu.jp/stock/search?view=result&page=1&sort_key=dividend_yield&order=desc&minimum_purchase_price[0]=min&minimum_purchase_price[1]=max&market_capitalization[0]=200&market_capitalization[1]=max&dividend_yield[0]=3&dividend_yield[1]=max&capital_adequacy_ratio[0]=40&capital_adequacy_ratio[1]=max&roe[0]=10&roe[1]=max&yutai_exist=1"
          target="_blank"
          id="dividendprediction_high_yutai"
          rel="noreferrer">
          <div
            className="mt-2 py-3 px-4 flex flex-row items-center bg-bluegray-50 border border-gray-300 rounded-lg"
            id={`click_${tag}_yutai`}>
            <div>
              <img
                src="/static/images/dividend/kohaito_icon3.svg"
                alt="高配当アイコン3"
              />
            </div>
            <div className="ml-2 text-sm font-bold">高配当×株主優待</div>
            <div className="icon-external-link text-xs ml-auto text-gray-500" />
          </div>
        </a>
      </div>
    </>
  );
};

// memo: 証券会社違いの同一銘柄をサマる
export const getSummaryIncomeGain = (
  data: IncomeGainItemProps[]
): IncomeGainItemProps[] => {
  const incomeGainList = Array.from(
    new Set(
      data.map(item => {
        return { symbol: item.symbol, symbolName: item.symbolName };
      })
    )
  );

  const uniqueArray = Array.from(
    new Map(incomeGainList.map(item => [item.symbolName, item])).values()
  );

  const targetIncomeGainList = uniqueArray.map(list => {
    return data.filter(
      data => data.symbol === list.symbol && data.symbolName === list.symbolName
    );
  });

  const summaryIncomeGainData = targetIncomeGainList
    .map(list => {
      const productTypeList = Array.from(
        new Set(list.map(item => item.productType))
      );

      const summaryIncomeGainData = productTypeList.map(productType => {
        const targetData = list.filter(
          list => list.productType === productType
        );

        const isAmountNull = targetData.every(data => data.amount === null);
        if (targetData.length === 1 || isAmountNull) return targetData[0];

        const summaryTargetData = targetData.reduce((acc, current, index) => {
          if (index === 0) return { ...current };

          const sumAmount = (acc.amount ?? 0) + (current.amount ?? 0);
          return { ...acc, amount: sumAmount };
        }, {} as IncomeGainItemProps);

        return summaryTargetData;
      }) as IncomeGainItemProps[];
      return summaryIncomeGainData as IncomeGainItemProps[];
    })
    .flat();

  const sortedSummaryIncomeGainData = getSortIncomeGainAmountData(
    summaryIncomeGainData
  );

  return sortedSummaryIncomeGainData;
};

const DividendDetailDeposit = (): React.ReactElement => {
  const { setWithBackIconWord } = useCustomizeHeaderContext();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(setWithBackIconWord, []);

  const { date, from, to } = useParams<{
    date: string;
    from: string;
    to: string;
  }>();

  const [incomeGainData, setIncomeGainData] = useState<
    IncomeGainItemProps[] | null
  >(null);
  const [displayIncomeGainData, setDisplayIncomeGainData] = useState<
    IncomeGainItemProps[] | null
  >(null);
  const [targetIncomeGainData, setTargetIncomeGainData] = useState<
    IncomeGainItemProps[] | null
  >(null);
  const [totalDividend, setTotalDividend] = useState<number | null>(null);

  const period = {
    from: yyyymmddToDate(from) ?? new Date(),
    to: yyyymmddToDate(to) ?? new Date(),
  };
  const dateStates = getMonthArray(period.from, period.to);
  const [selectedDate, setSelectedDate] = useState<string>(date);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const getYm = (date: string): string => {
    const data = yyyymmddToDate(date);
    return formatDate(data, 'yyyyMM');
  };

  useEffect(() => {
    (async () => {
      setIsLoading(true);

      const queryFrom = getYm(from);
      const queryTo = getYm(to);

      const ret_ = await getIncomeGain(queryFrom, queryTo);
      const ret = {
        from: ret_.From,
        to: ret_.To,
        items: ret_.Items.map(item => {
          return {
            account: item.Account,
            brokerID: item.BrokerID,
            brokerName: item.BrokerName,
            symbol: item.Symbol,
            symbolName: item.SymbolName,
            productType: item.ProductType,
            amount: item.Amount,
            date: item.Date,
            isin: item.ISIN,
            hasNew: item.HasNew,
          };
        }) as IncomeGainItemProps[],
      } as IncomeGainProps;
      setIncomeGainData(ret.items);

      const targetIncomeGainData = ret.items.filter(data => {
        const dateYm = getYm(data.date);
        return dateYm === date;
      });

      const summaryTargetIncomeGainData = getSummaryIncomeGain(
        targetIncomeGainData
      );

      const sortedTargetIncomeGainData = getSortIncomeGainAmountData(
        summaryTargetIncomeGainData
      );
      setDisplayIncomeGainData(sortedTargetIncomeGainData);

      const totalDividend = getSummaryIncomeGainAmount(
        sortedTargetIncomeGainData
      );
      setTotalDividend(totalDividend);
      setIsLoading(false);

      const targetIncomeGainData_ = ret.items.filter(item => {
        const targetDate_ = yyyymmddToDate(item.date);
        if (targetDate_ === null) return false;
        const targetDate = formatDate(targetDate_, 'yyyyMM');
        return targetDate === selectedDate;
      });
      setTargetIncomeGainData(targetIncomeGainData_);
    })();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (incomeGainData === null) return;

    const targetIncomeGainData = incomeGainData.filter(data => {
      const dateYm = getYm(data.date);
      return dateYm === selectedDate;
    });

    const summaryTargetIncomeGainData = getSummaryIncomeGain(
      targetIncomeGainData
    );

    const sortedTargetIncomeGainData = getSortIncomeGainAmountData(
      summaryTargetIncomeGainData
    );
    setDisplayIncomeGainData(sortedTargetIncomeGainData);

    const totalDividend = getSummaryIncomeGainAmount(
      sortedTargetIncomeGainData
    );
    setTotalDividend(totalDividend);

    const targetIncomeGainData_ = incomeGainData.filter(item => {
      const targetDate_ = yyyymmddToDate(item.date);
      if (targetDate_ === null) return false;
      const targetDate = formatDate(targetDate_, 'yyyyMM');
      return targetDate === selectedDate;
    });
    setTargetIncomeGainData(targetIncomeGainData_);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  if (isIosApp()) return <Redirect to="/home" />;

  if (
    incomeGainData === null ||
    displayIncomeGainData === null ||
    targetIncomeGainData === null ||
    totalDividend === null ||
    isLoading
  )
    return <DividendDepositLoading str={'銘柄'} />;

  return (
    <>
      <DividendHeader {...{ type: 'deposit' }} />
      <div className="bg-white text-bluegray-700">
        <div
          className="relative w-full flex flex-row justify-center items-center mx-auto pt-6"
          style={{ width: '135px' }}>
          <select
            className={`h-full w-full pl-2 appearance-none bg-transparent text-base font-bold cursor-pointer`}
            value={selectedDate}
            onChange={(e): void => {
              setSelectedDate(e.currentTarget.value);
            }}>
            {dateStates.map((s, i) => {
              return (
                <option key={i} value={s.toString()}>
                  {formatYM(s)}
                </option>
              );
            })}
          </select>
          <div
            className={`absolute icon-arrow_drop_down pointer-events-none p-0.5 text-2xl`}
            style={{ right: '6%' }}></div>
        </div>
        <div className="px-4 pt-2 text-center text-xs">
          <span className="mr-1 text-2xl font-bold">
            {formatNumber(totalDividend, {})}
          </span>
          円
        </div>

        <div className="my-2 mx-1">
          {displayIncomeGainData.length === 0 ? (
            <DividendPieChartNoData />
          ) : (
            <DividendPieChart {...{ incomeGainData: displayIncomeGainData }} />
          )}
        </div>
        <DividendDepositModal />
        <DividendDetailList
          {...{
            displayIncomeGainData,
            incomeGainData: targetIncomeGainData,
            totalDividend,
          }}
        />
      </div>
      <UpdateTimeComment />
      <HightDividendList {...{ tag: 'deposit', className: 'mt-8 ml-4' }} />
    </>
  );
};

export default DividendDetailDeposit;
